import React, { useEffect, useState } from 'react';
import { FaSignInAlt, FaEnvelope, FaLock, FaSignOutAlt, FaWarehouse } from 'react-icons/fa';
import { stockLogin, stockApi, STOCK_TOKEN_KEY } from '../services/stockApi';
import Loader from '../components/Loader';
import InventoryDashboard from '../components/InventoryDashboard';
import './Auth.css';
import './Admin.css';
import { useT } from '../i18n/LanguageContext';

const T = {
    en: {
        loadingStockInventory: 'Loading stock inventory',
        noAccessError: 'This account does not have access to the stock inventory.',
        loginFailed: 'Login failed. Please try again.',
        accessHeading: 'Stock Inventory Access',
        accessDescription: 'Sign in to view and manage stock. This session is separate from the main admin panel.',
        emailLabel: 'Email',
        emailPlaceholder: 'Enter your email',
        passwordLabel: 'Password',
        passwordPlaceholder: 'Enter your password',
        signingIn: 'Signing in',
        signIn: 'Sign In',
        needAccessPrefix: 'Need access? Ask an admin to grant the ',
        stockInventory: 'Stock Inventory',
        needAccessSuffix: ' permission to your account.',
        signedInAs: 'Signed in as ',
        signOut: 'Sign out',
    },
    fr: {
        loadingStockInventory: 'Chargement de l’inventaire du stock',
        noAccessError: 'Ce compte n’a pas accès à l’inventaire du stock.',
        loginFailed: 'Échec de la connexion. Veuillez réessayer.',
        accessHeading: 'Accès à l’inventaire du stock',
        accessDescription: 'Connectez-vous pour consulter et gérer le stock. Cette session est distincte du panneau d’administration principal.',
        emailLabel: 'E-mail',
        emailPlaceholder: 'Saisissez votre e-mail',
        passwordLabel: 'Mot de passe',
        passwordPlaceholder: 'Saisissez votre mot de passe',
        signingIn: 'Connexion en cours',
        signIn: 'Se connecter',
        needAccessPrefix: 'Besoin d’un accès ? Demandez à un administrateur d’accorder l’autorisation ',
        stockInventory: 'Inventaire du stock',
        needAccessSuffix: ' à votre compte.',
        signedInAs: 'Connecté en tant que ',
        signOut: 'Se déconnecter',
    },
};

function decodeStockToken(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 <= Date.now()) return null;
        const isAdmin = payload.role === 'admin';
        const perms = Array.isArray(payload.permissions) ? payload.permissions : [];
        if (!isAdmin && !perms.includes('inventory')) return null;
        return {
            id: payload.id,
            name: payload.name,
            email: payload.email,
            role: payload.role,
            permissions: perms,
        };
    } catch {
        return null;
    }
}

function StockInventory() {
    const t = useT(T);
    const [user, setUser] = useState(null);
    const [bootLoading, setBootLoading] = useState(true);
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem(STOCK_TOKEN_KEY);
        if (token) {
            const u = decodeStockToken(token);
            if (u) setUser(u);
            else localStorage.removeItem(STOCK_TOKEN_KEY);
        }
        setBootLoading(false);
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            const res = await stockLogin(formData);
            const token = res.data.token;
            const decoded = decodeStockToken(token);
            if (!decoded) {
                setError(t.noAccessError);
                setSubmitting(false);
                return;
            }
            localStorage.setItem(STOCK_TOKEN_KEY, token);
            setUser(decoded);
        } catch (err) {
            setError(err.response?.data?.error || t.loginFailed);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSignOut = () => {
        localStorage.removeItem(STOCK_TOKEN_KEY);
        setUser(null);
        setFormData({ email: '', password: '' });
    };

    if (bootLoading) return <Loader fullscreen label={t.loadingStockInventory} />;

    if (!user) {
        return (
            <div className="auth-page">
                <div className="auth-container">
                    <div className="auth-header">
                        <FaWarehouse className="auth-icon" />
                        <h2>{t.accessHeading}</h2>
                        <p>{t.accessDescription}</p>
                    </div>

                    {error && <div className="auth-error">{error}</div>}

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-group">
                            <label><FaEnvelope /> {t.emailLabel}</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder={t.emailPlaceholder}
                                required
                                autoComplete="username"
                            />
                        </div>

                        <div className="form-group">
                            <label><FaLock /> {t.passwordLabel}</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder={t.passwordPlaceholder}
                                required
                                autoComplete="current-password"
                            />
                        </div>

                        <button type="submit" className="auth-btn" disabled={submitting}>
                            {submitting ? <Loader inline label={t.signingIn} /> : <><FaSignInAlt /> {t.signIn}</>}
                        </button>
                    </form>

                    <p className="auth-switch">
                        {t.needAccessPrefix}<strong>{t.stockInventory}</strong>{t.needAccessSuffix}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page stock-inventory-standalone">
            <div className="admin-content" style={{ width: '100%' }}>
                <div className="standalone-topbar">
                    <span className="standalone-user">
                        {t.signedInAs}<strong>{user.name}</strong> ({user.email})
                    </span>
                    <button className="admin-btn danger" onClick={handleSignOut}>
                        <FaSignOutAlt /> {t.signOut}
                    </button>
                </div>
                <InventoryDashboard api={stockApi} user={user} />
            </div>
        </div>
    );
}

export default StockInventory;
